/**
 * Reusable structured "Weekly Activity" block editor.
 * Activities recur weekly by day-of-week, with full venue + address info.
 */
import { Calendar, Trash2, Plus } from "lucide-react";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const inp = "w-full bg-black border border-white/15 focus:border-[#D4AF37] outline-none px-3 py-2.5 rounded text-sm text-white";

const newActivity = () => ({
  _id: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).slice(2),
  title: "",
  day_of_week: "Sunday",
  start_time: "09:00",
  end_time: "10:30",
  venue_name: "",
  address_line1: "",
  address_line2: "",
  city: "",
  postcode: "",
  country: "United Kingdom",
  maps_url: "",
  description: "",
  published: true,
});

export default function ActivitiesBlockEditor({ items = [], onChange }) {
  const update = (i, patch) => {
    const arr = [...items];
    arr[i] = { ...arr[i], ...patch };
    onChange(arr);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, newActivity()]);

  return (
    <div data-testid="block-weekly-activities" className="luxury-card rounded-xl p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-serif-display text-xl flex items-center gap-2"><Calendar size={16} className="text-[#D4AF37]" /> Weekly Activities</div>
          <div className="text-xs text-neutral-500 mt-1">Recurring weekly by day. Times and address are shown on your public site.</div>
        </div>
        <button data-testid="block-wa-add" onClick={add} className="btn-ghost-gold text-[10px] tracking-widest uppercase px-3 py-1.5 rounded inline-flex items-center gap-1"><Plus size={11} /> Add</button>
      </div>

      <div className="mt-4 space-y-4">
        {items.map((row, i) => (
          <div key={row._id || `wa-${i}`} data-testid={`block-wa-row-${i}`} className="border border-white/5 rounded-lg p-4 bg-black/30 space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Activity title">
                <input data-testid={`wa-${i}-title`} value={row.title || ""} onChange={(e) => update(i, { title: e.target.value })} className={inp} placeholder="Sunday Service" />
              </Field>
              <Field label="Day of week">
                <select data-testid={`wa-${i}-day`} value={row.day_of_week || "Sunday"} onChange={(e) => update(i, { day_of_week: e.target.value })} className={inp}>
                  {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Start time"><input data-testid={`wa-${i}-start`} type="time" value={row.start_time || ""} onChange={(e) => update(i, { start_time: e.target.value })} className={inp} /></Field>
              <Field label="End time"><input data-testid={`wa-${i}-end`} type="time" value={row.end_time || ""} onChange={(e) => update(i, { end_time: e.target.value })} className={inp} /></Field>
              <Field label="Venue name"><input data-testid={`wa-${i}-venue`} value={row.venue_name || ""} onChange={(e) => update(i, { venue_name: e.target.value })} className={inp} placeholder="Main Auditorium" /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Address line 1"><input value={row.address_line1 || ""} onChange={(e) => update(i, { address_line1: e.target.value })} className={inp} /></Field>
              <Field label="Address line 2"><input value={row.address_line2 || ""} onChange={(e) => update(i, { address_line2: e.target.value })} className={inp} /></Field>
              <Field label="City"><input value={row.city || ""} onChange={(e) => update(i, { city: e.target.value })} className={inp} /></Field>
              <Field label="Postcode"><input value={row.postcode || ""} onChange={(e) => update(i, { postcode: e.target.value })} className={inp} /></Field>
              <Field label="Country"><input value={row.country || ""} onChange={(e) => update(i, { country: e.target.value })} className={inp} /></Field>
              <Field label="Google Maps link"><input value={row.maps_url || ""} onChange={(e) => update(i, { maps_url: e.target.value })} className={inp} placeholder="https://maps.google.com/..." /></Field>
            </div>
            <Field label="Description"><textarea rows={2} value={row.description || ""} onChange={(e) => update(i, { description: e.target.value })} className={inp} /></Field>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-neutral-300">
                <input type="checkbox" checked={!!row.published} onChange={(e) => update(i, { published: e.target.checked })} /> Published
              </label>
              <button data-testid={`wa-${i}-remove`} onClick={() => remove(i)} className="text-red-400 text-xs tracking-widest uppercase inline-flex items-center gap-1"><Trash2 size={11} /> Remove</button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-xs text-neutral-500">No weekly activities yet — click Add.</div>}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] tracking-widest uppercase text-neutral-400">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
